<template>
  <div class="page-wrapper" v-if="themeList.length > 0">
    <!-- Page head -->
    <div class="learning-name">{{ currentLearningType.name }}</div>
    <h1 class="theme-list-title">THEME SELECTION</h1>
    <p class="theme-list-sub">Please select a theme</p>

    <!-- Theme card list -->
    <ul class="theme-list">
      <li v-for="theme in themeList" :key="theme.id" class="theme-column">
        <p class="icon-title">{{ theme.name }}</p>
        <div
          :class="{ 'img-wrap': true, clickable: theme.isOpen }"
          :style="{ color: theme.theme_color }"
          @click="goDetails(theme)"
          @keypress.enter="goDetails(theme)"
        >
          <img
            class="default"
            :src="$path.resolveImage(theme.image, $assets.image.default['default-blank-theme.svg'])"
            alt=""
          />
          <img
            class="hover"
            :src="$path.resolveImage(theme.hover_image, $assets.image.default['hub_default_bg.jpg'])"
            alt=""
          />

          <div class="corner" v-if="!theme.isOpen" />
          <div class="coming-soon" v-if="!theme.isOpen">
            <p>Coming</p>
            <p>Soon</p>
          </div>
        </div>
        <p class="icon-summ">{{ theme.summary }}</p>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';

import $assets from '@/assets';
import ThemeService from '@/services/theme';
import useSessionStore from '@/stores/session';
import $path from '@/utils/path';

const props = defineProps({
  sectorId: {
    type: String,
    required: true,
  },
});

const $router = useRouter();
const SessionStore = useSessionStore();
const themeList = ref([]);

const currentLearningType = computed(() => {
  return SessionStore.HUBSettings.currentLearningType;
});

async function getThemeList() {
  const themeResults = await ThemeService.getThemes(props.sectorId);
  themeList.value = themeResults.records.map((r) => ({ ...r, isOpen: r.theme_status === 'open' }));
}

function goDetails(theme) {
  if (!theme.isOpen) return;

  $router.push({
    name: 'themeDetails',
    params: { themeId: theme.id },
  });
}

watch(
  () => props.sectorId,
  () => {
    getThemeList();
  },
  { immediate: true }
);
</script>

<style scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  height: 100%;
  color: #808285;
}

.learning-name {
  margin: 3em 0 1em;
  font-size: 1.2em;
  font-weight: 700;
}

h1.theme-list-title {
  margin: 0;
  padding: 0.2em;
  border-bottom: 1px solid #a7a9ac;
}
.theme-list-sub {
  margin: 0;
  padding: 0.2em;
  font-size: 1.2em;
}

ul.theme-list {
  display: grid;
  justify-content: space-evenly;
  grid-template-columns: repeat(auto-fit, 80%);
  row-gap: 3em;

  padding: 3em 0;
  width: 100%;
}
.theme-column {
  text-align: center;
}

/** Card text */
.theme-column .icon-title {
  margin: 0;
  font-size: 1.2em;
}
.theme-column .icon-summ {
  margin: 0;
  font-size: 1.1em;
}

/** Card graphic */
.theme-column .img-wrap {
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;
  margin: 0.5em 0;
  border: 1px solid #ccc;
  border-radius: 0.8em;
  padding: 3.6em 0;
  overflow: hidden;

  transition-property: border-color, background-color;
  transition-duration: 0.4s;
}
.theme-column .img-wrap.clickable:hover {
  border-color: currentColor;
  background-color: currentColor;
  box-shadow: 0 0 12px 1px rgba(0, 0, 0, 0.3);
}
.theme-column .img-wrap img {
  width: 80%;
  transition: opacity 0.3s;
}
.theme-column .img-wrap.clickable:hover img {
  opacity: 0;
}
.theme-column .img-wrap img.hover {
  position: relative;
  margin-left: -80%;
  width: 80%;
  opacity: 0;
  transition: opacity 0.3s;
}
.theme-column .img-wrap.clickable:hover img.hover {
  opacity: 1;
}

/** Coming soon label */
.theme-column .img-wrap .corner {
  position: absolute;
  right: 0;
  top: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 6em 6em 0;
  border-color: transparent #777 transparent transparent;
}
.coming-soon {
  position: absolute;
  right: 0.4em;
  top: 0.4em;
  width: 4em;
  height: 4em;
  color: #fff;
  font-size: 0.9em;
}
.coming-soon > p {
  margin: 0;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  ul.theme-list {
    grid-template-columns: repeat(auto-fit, 38%);
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .learning-name {
    margin: 4em 0 1em;
  }

  ul.theme-list {
    grid-template-columns: repeat(auto-fill, 16em);
    width: min(90%, 90em);
    padding: 4em 0;
  }
}
</style>
